import { useEffect, useRef } from 'react';
import { motion, useInView, useMotionValue, useTransform, animate } from 'framer-motion';
import { Props_StatsSnippet } from './Stats.props';
import { CompileMarkdown } from "../../../../Shared/AttraxTs/React/ReactSnippetMDCompiler";

const SnippetStats = (props: Props_StatsSnippet) => {
    const { heading, repeatable_items, background } = props;

    const backgroundMap = {
        'white': '#ffffff',
        'covea-dark-blue': '#122147',
        'covea-light-green': '#d6eac1',
        'covea-light-red': '#efded3',
        'covea-light-purple': '#e2d3f2',
        'covea-light-turquoise': '#c8eaed',
        'covea-light-yellow': '#eae8ad',
        'MAAF-dark-blue': '#0091a7',
        'MAAF-light-blue': '#e5f3f5',
        'MAAF-light-red': '#fef6f2',
        'MMA-light-green': '#d6eceb',
        'MMA-light-orange': '#f2e1b5',
        'MMA-dark-blue': '#1d3b6d',
        'GMF-dark-blue': '#144390',
        'GMF-light-blue': '#dfedff',
        'GMF-light-orange': '#fff4ed',
        'Fidelia-dark-green': '#269b38',
        'Fidelia-light-blue': '#e1f0fb',
        'Fidelia-light-green': '#ebf2e7'
    }
    

    const backgroundKey = background?.colour.value || 'white'
    const backgroundValue = backgroundMap[backgroundKey as keyof typeof backgroundMap] || backgroundMap['white']
    const darkBackgrounds = ['covea-dark-blue', 'MAAF-dark-blue', 'MMA-dark-blue', 'GMF-dark-blue', 'Fidelia-dark-green']
    const useWhiteText = darkBackgrounds.includes(backgroundKey) ? 'stats-snippet--white-text' : ''
    
    return (
        <section className={`stats-snippet ${useWhiteText}`} style={{ backgroundColor: backgroundValue }}>
            <div className={`stats-snippet__container`}>
                {heading?.content?.value &&
                    <h2 className="stats-snippet__heading">{heading.content.value}</h2>
                }
                <ul className="stats-snippet__items">
                    {repeatable_items?.map((item, index) => (
                        <li key={`stat-item-${index}`} className="stats-snippet__item">
                            {item.stat?.value &&
                                <AnimatedStat value={item.stat.value} symbol={item.symbol?.value} symbolPosition={item.symbolPosition?.value} />
                            }
                            {item.text?.value &&
                                <div className="stats-snippet__text">
                                    {CompileMarkdown(item.text.value)}
                                </div>
                            }
                        </li>
                    ))}
                </ul>
            </div>
        </section>
    );
};

const AnimatedStat = ({ value, symbol, symbolPosition = 'after' }: { value: string; symbol?: string; symbolPosition?: string }) => {
    const ref = useRef<HTMLDivElement>(null);
    const isInView = useInView(ref, { once: true });
    const motionValue = useMotionValue(0);

    // Detect if comma is decimal separator (e.g., "3,8") or thousands separator (e.g., "1,234")
    const hasComma = value.includes(',');
    const isDecimalComma = hasComma && /,\d{1,2}(?!\d)/.test(value); // Comma followed by 1-2 digits at end
    
    // Parse the value: replace comma with dot if decimal, remove if thousands separator
    const numericValue = parseFloat(isDecimalComma ? value.replace(',', '.') : value.replace(/,/g, ''));
    
    const symbolPositionClass = symbolPosition === 'before' ? 'stats-snippet__stat--symbol-before' : 'stats-snippet__stat--symbol-after';

    useEffect(() => {
        if (isInView) {
            const controls = animate(motionValue, numericValue, {
                duration: 2,
                ease: 'easeOut'
            });
            return controls.stop;
        }
    }, [isInView, numericValue, motionValue]);

    const rounded = useTransform(motionValue, (latest) => {
        const hasCommas = value.includes(',');
        
        if (!hasCommas) {
            return Math.round(latest).toString();
        }
        
        const parts = value.split(',');
        const afterComma = parts[parts.length - 1];
        const isDecimalComma = afterComma.length <= 2 && /^\d+$/.test(afterComma);
        
        
        console.log('AnimatedStat transform:', { value, latest, afterComma, isDecimalComma });
        
        if (isDecimalComma) {
            const decimalPlaces = afterComma.length;
            const result = latest.toFixed(decimalPlaces).replace('.', ',');
            console.log('Decimal comma result:', result);
            return result;
        } else {
            const result = Math.round(latest).toLocaleString('en-US');
            console.log('Thousands separator result:', result);
            return result;
        }
    });

    return (
        <div ref={ref} className={`stats-snippet__stat ${symbolPositionClass}`}>
            <motion.span>{rounded}</motion.span>
            {symbol && <span className="stats-snippet__symbol">{symbol}</span>}
        </div>
    );
};

export default SnippetStats;